import { useState, Fragment } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { GuestMatch } from '@/hooks/useStripeCleanup';

interface GuestMatchesTableProps {
  matches: GuestMatch[];
  selectedIds: Set<string>;
  onToggleSelection: (guestId: string) => void;
}

export function GuestMatchesTable({
  matches,
  selectedIds,
  onToggleSelection,
}: GuestMatchesTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (guestId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(guestId)) {
        newSet.delete(guestId);
      } else {
        newSet.add(guestId);
      }
      return newSet;
    });
  };

  const getScoreBadgeVariant = (score: number) => {
    if (score >= 3) return 'default';
    if (score >= 2) return 'secondary';
    return 'outline';
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]"></TableHead>
            <TableHead className="w-[50px]">
              <Checkbox 
                checked={selectedIds.size === matches.length && matches.length > 0}
                onCheckedChange={(checked) => {
                  if (checked) {
                    matches.forEach(m => onToggleSelection(m.guest.id));
                  } else {
                    matches.forEach(m => {
                      if (selectedIds.has(m.guest.id)) {
                        onToggleSelection(m.guest.id);
                      }
                    });
                  }
                }}
              />
            </TableHead>
            <TableHead>Guest Email</TableHead>
            <TableHead></TableHead>
            <TableHead>Possível Dono</TableHead>
            <TableHead className="text-center">Score</TableHead>
            <TableHead>Partes Matcheadas</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {matches.map((match) => (
            <Fragment key={match.guest.id}>
              <TableRow 
                className={selectedIds.has(match.guest.id) ? 'bg-muted/50' : ''}
              >
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => toggleRow(match.guest.id)}
                  >
                    {expandedRows.has(match.guest.id) ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </TableCell>
                <TableCell>
                  <Checkbox 
                    checked={selectedIds.has(match.guest.id)}
                    onCheckedChange={() => onToggleSelection(match.guest.id)}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-mono text-sm">{match.guest.email}</span>
                    <span className="text-xs text-muted-foreground">
                      {match.guest.name || 'Guest'} • {match.guest.charges_count} cobranças
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{match.possible_owner.name}</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {match.possible_owner.email || 'Sem email'}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={getScoreBadgeVariant(match.possible_owner.match_score)}>
                    {match.possible_owner.match_score}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {match.possible_owner.matched_parts.map((part, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {part}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
              
              {/* Expanded Details */}
              {expandedRows.has(match.guest.id) && (
                <TableRow className="bg-muted/30">
                  <TableCell colSpan={7} className="p-4">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium mb-2 text-sm">Guest (será mesclado)</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-muted-foreground">ID:</span> <span className="font-mono">{match.guest.id}</span></p>
                          <p><span className="text-muted-foreground">Email:</span> {match.guest.email}</p>
                          <p><span className="text-muted-foreground">Nome:</span> {match.guest.name || 'Guest'}</p>
                          <p><span className="text-muted-foreground">Cobranças:</span> {match.guest.charges_count}</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2 text-sm">Dono (receberá dados)</h4>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-muted-foreground">ID:</span> <span className="font-mono">{match.possible_owner.id}</span></p>
                          <p><span className="text-muted-foreground">Email:</span> {match.possible_owner.email || 'N/A'}</p>
                          <p><span className="text-muted-foreground">Nome:</span> {match.possible_owner.name}</p>
                          <p><span className="text-muted-foreground">Score:</span> {match.possible_owner.match_score} partes matcheadas</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Ação sugerida:</strong> Mover métodos de pagamento do Guest para o Dono, 
                        e arquivar/deletar o Guest.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
